import React, { useState, useEffect } from 'react';
import axios from 'axios';

export default function FleetPKmNotings() {
    const API_URL = process.env.REACT_APP_API_URL;

    // Form state
    const [driverId, setDriverId] = useState('');
    const [vehiclePlate, setVehiclePlate] = useState('');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [startKm, setStartKm] = useState('');
    const [endKm, setEndKm] = useState('');
    const [routeDescription, setRouteDescription] = useState('');
    
    // Data state
    const [records, setRecords] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingDrivers, setLoadingDrivers] = useState(false);
    const [loadingVehicles, setLoadingVehicles] = useState(false);
    const [msg, setMsg] = useState('');
    const [error, setError] = useState('');

    // Calculate total KM
    const totalKm = startKm && endKm ? Math.max(0, parseInt(endKm) - parseInt(startKm)) : 0;

    // Mock vehicles fallback (pretend FleetP vehicles)
    const mockVehicles = [
        { plate: 'Vehicle A - DXB 1234', model: 'Toyota Hiace' },
        { plate: 'Vehicle B - DXB 5678', model: 'Nissan Urvan' },
        { plate: 'Vehicle C - AUH 9012', model: 'Toyota Coaster' },
        { plate: 'Vehicle D - SHJ 3456', model: 'Mitsubishi Rosa' },
        { plate: 'Vehicle E - AJM 7890', model: 'Hyundai H1' },
    ];

    useEffect(() => {
        fetchDrivers();
        fetchVehicles();
        fetchRecords();
    }, []);

    const fetchDrivers = async () => {
        setLoadingDrivers(true);
        try {
            const token = sessionStorage.getItem('token');
            // Try FleetP API first
            const response = await axios.get(
                `${API_URL}shypvdriverapp/api/integrations/default/FLEETP/drivers`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const data = response.data?.data || [];
            if (Array.isArray(data) && data.length > 0) {
                setDrivers(data.map(d => ({
                    id: d.id,
                    name: d.name || `Driver ${d.id}`,
                    phone: d.phone || '',
                    license: d.license_no || '',
                })));
            } else {
                await fetchDriversFallback();
            }
        } catch (err) {
            console.error('Error fetching FleetP drivers, trying local:', err);
            await fetchDriversFallback();
        }
        setLoadingDrivers(false);
    };

    const fetchDriversFallback = async () => {
        try {
            const token = sessionStorage.getItem('token');
            const response = await axios.get(
                `${API_URL}shypvdriverapp/driver/getAllDrivers`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const data = response.data?.data || response.data?.responseObject || [];
            if (Array.isArray(data) && data.length > 0) {
                setDrivers(data.map(d => ({
                    id: d.driverId,
                    name: d.name || d.userName || `Driver ${d.driverId}`,
                })));
            }
        } catch (err) {
            console.error('Error fetching local drivers:', err);
        }
    };

    const fetchVehicles = async () => {
        setLoadingVehicles(true);
        try {
            const token = sessionStorage.getItem('token');
            // Try FleetP API first
            const response = await axios.get(
                `${API_URL}shypvdriverapp/api/integrations/default/FLEETP/vehicles`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const data = response.data?.data || [];
            if (Array.isArray(data) && data.length > 0) {
                setVehicles(data.map(v => ({
                    plate: v.plate_number || v.id,
                    model: v.model || 'Vehicle',
                })));
            } else {
                await fetchVehiclesFallback();
            }
        } catch (err) {
            console.error('Error fetching FleetP vehicles, trying local:', err);
            await fetchVehiclesFallback();
        }
        setLoadingVehicles(false);
    };

    const fetchVehiclesFallback = async () => {
        try {
            const token = sessionStorage.getItem('token');
            const response = await axios.get(
                `${API_URL}shypvdriverapp/vehicle/getVehicles`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const data = response.data?.data || response.data?.responseObject || [];
            if (Array.isArray(data) && data.length > 0) {
                setVehicles(data.map(v => ({
                    plate: v.vehicle_registration_number || v.vehicleRegistrationNumber || v.vehicle_name || `V-${v.vehicle_code}`,
                    model: v.vehicle_name || v.vehicleName || 'Vehicle',
                })));
            } else {
                setVehicles(mockVehicles);
            }
        } catch (err) {
            console.error('Error fetching local vehicles, using mock:', err);
            setVehicles(mockVehicles);
        }
    };

    const fetchRecords = async () => {
        try {
            const token = sessionStorage.getItem('token');
            const today = new Date().toISOString().split('T')[0];
            const response = await axios.get(
                `${API_URL}shypvdriverapp/driver-attendance/all?dateFrom=${today}&dateTo=${today}&size=50`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const pageData = response.data?.data || response.data?.responseObject;
            const data = pageData?.content || pageData || [];
            if (Array.isArray(data)) {
                // Filter only records that have KM readings
                const kmRecords = data.filter(r => r.kmReading && parseFloat(r.kmReading) > 0);
                setRecords(kmRecords.map(r => ({
                    id: r.id,
                    driverId: r.driverId,
                    driverName: r.driverName || `Driver ${r.driverId}`,
                    vehiclePlate: r.vehiclePlateNumber || '-',
                    kmReading: parseFloat(r.kmReading),
                    date: r.checkInTime ? new Date(r.checkInTime).toISOString().split('T')[0] : '-',
                    syncStatus: 'synced',
                })));
            }
        } catch (err) {
            console.error('Error fetching KM records:', err);
        }
    };

    // Save records to localStorage whenever they change (backup)
    useEffect(() => {
        if (records.length > 0) {
            localStorage.setItem('fleetp_km_records', JSON.stringify(records));
        }
    }, [records]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!driverId || !vehiclePlate || !date || !startKm || !endKm) {
            setError('Please fill in all required fields');
            return;
        }
        
        if (parseInt(endKm) < parseInt(startKm)) {
            setError('End KM cannot be less than Start KM');
            return;
        }
        
        setLoading(true);
        setError('');
        setMsg('');
        
        try {
            const token = sessionStorage.getItem('token');
            const totalKmVal = parseInt(endKm) - parseInt(startKm);
            
            // Save as attendance record with KM data
            const payload = {
                driverId: parseInt(driverId),
                vehiclePlateNumber: vehiclePlate,
                kmReading: totalKmVal,
                checkInDateTime: `${date}T08:00:00`,
            };

            await axios.post(
                `${API_URL}shypvdriverapp/driver-attendance/manual`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setMsg('KM record saved & synced to FleetP!');
            
            // Reset form
            setDriverId('');
            setVehiclePlate('');
            setStartKm('');
            setEndKm('');
            setRouteDescription('');
            
            // Refresh records
            fetchRecords();
        } catch (err) {
            console.error('Error saving KM record:', err);
            setError(err.response?.data?.message || 'Failed to save KM record. Please try again.');
        }
        setLoading(false);
    };

    const getRecentRecords = () => {
        return records.slice(0, 10);
    };

    const getTotalKmToday = () => {
        return records.reduce((sum, r) => sum + (r.kmReading || 0), 0);
    };

    const getSyncStatusBadge = (status) => {
        if (status === 'synced') {
            return (
                <span style={styles.syncedBadge}>
                    <i className="fa fa-check" style={{ marginRight: '4px' }}></i>
                    Synced
                </span>
            );
        }
        return (
            <span style={styles.pendingBadge}>
                <i className="fa fa-clock-o" style={{ marginRight: '4px' }}></i>
                Pending
            </span>
        );
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h2 style={styles.title}>
                    <i className="fa fa-road" style={{ marginRight: '10px', color: '#1976d2' }}></i>
                    FleetP KM Notings
                </h2>
                <p style={styles.subtitle}>
                    Record vehicle kilometer readings and trip information
                </p>
            </div>

            {/* Stats Cards */}
            <div style={styles.statsRow}>
                <div style={styles.statCard}>
                    <div style={styles.statIcon}>
                        <i className="fa fa-tachometer" style={{ fontSize: '24px', color: '#1976d2' }}></i>
                    </div>
                    <div style={styles.statContent}>
                        <div style={styles.statValue}>{getTotalKmToday().toLocaleString()}</div>
                        <div style={styles.statLabel}>KM Today</div>
                    </div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statIcon}>
                        <i className="fa fa-file-text-o" style={{ fontSize: '24px', color: '#28a745' }}></i>
                    </div>
                    <div style={styles.statContent}>
                        <div style={styles.statValue}>{records.filter(r => r.date === date).length}</div>
                        <div style={styles.statLabel}>Records Today</div>
                    </div>
                </div>
                <div style={styles.statCard}>
                    <div style={styles.statIcon}>
                        <i className="fa fa-car" style={{ fontSize: '24px', color: '#17a2b8' }}></i>
                    </div>
                    <div style={styles.statContent}>
                        <div style={styles.statValue}>{vehicles.length}</div>
                        <div style={styles.statLabel}>Vehicles</div>
                    </div>
                </div>
            </div>

            {/* Messages */}
            {msg && (
                <div style={styles.successAlert}>
                    <i className="fa fa-check-circle" style={{ marginRight: '8px' }}></i>
                    {msg}
                </div>
            )}
            {error && (
                <div style={styles.errorAlert}>
                    <i className="fa fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
                    {error}
                </div>
            )}

            <div style={styles.gridContainer}>
                {/* Form Section */}
                <div style={styles.card}>
                    <h3 style={styles.cardTitle}>
                        <i className="fa fa-plus-circle" style={{ marginRight: '8px', color: '#28a745' }}></i>
                        Record KM
                    </h3>
                    
                    <form onSubmit={handleSubmit}>
                        <div style={styles.formRow}>
                            <div style={styles.formGroupHalf}>
                                <label style={styles.label}>Driver *</label>
                                <select
                                    value={driverId}
                                    onChange={(e) => setDriverId(e.target.value)}
                                    style={styles.select}
                                    required
                                >
                                    <option value="">{loadingDrivers ? 'Loading drivers...' : 'Select Driver'}</option>
                                    {drivers.map(driver => (
                                        <option key={driver.id} value={driver.id}>
                                            {driver.name} (ID: {driver.id})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div style={styles.formGroupHalf}>
                                <label style={styles.label}>Vehicle Plate *</label>
                                <select
                                    value={vehiclePlate}
                                    onChange={(e) => setVehiclePlate(e.target.value)}
                                    style={styles.select}
                                    required
                                >
                                    <option value="">{loadingVehicles ? 'Loading vehicles from FleetP...' : 'Select Vehicle'}</option>
                                    {vehicles.map((vehicle, idx) => (
                                        <option key={idx} value={vehicle.plate}>
                                            {vehicle.plate} — {vehicle.model}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Date *</label>
                            <input
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                style={styles.input}
                                required
                            />
                        </div>

                        <div style={styles.formRow}>
                            <div style={styles.formGroupHalf}>
                                <label style={styles.label}>
                                    <i className="fa fa-play" style={{ marginRight: '6px', color: '#28a745' }}></i>
                                    Start KM *
                                </label>
                                <input
                                    type="number"
                                    value={startKm}
                                    onChange={(e) => setStartKm(e.target.value)}
                                    placeholder="e.g., 45000"
                                    style={styles.input}
                                    min="0"
                                    required
                                />
                            </div>
                            <div style={styles.formGroupHalf}>
                                <label style={styles.label}>
                                    <i className="fa fa-stop" style={{ marginRight: '6px', color: '#dc3545' }}></i>
                                    End KM *
                                </label>
                                <input
                                    type="number"
                                    value={endKm}
                                    onChange={(e) => setEndKm(e.target.value)}
                                    placeholder="e.g., 45150"
                                    style={styles.input}
                                    min="0"
                                    required
                                />
                            </div>
                        </div>

                        {/* Total KM Display */}
                        <div style={styles.totalKmBox}>
                            <div style={styles.totalKmLabel}>Total KM</div>
                            <div style={styles.totalKmValue}>{totalKm.toLocaleString()} km</div>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Route/Trip Description</label>
                            <textarea
                                value={routeDescription}
                                onChange={(e) => setRouteDescription(e.target.value)}
                                placeholder="e.g., Dubai Marina to Al Quoz delivery route"
                                style={styles.textarea}
                                rows={3}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            style={{
                                ...styles.submitButton,
                                opacity: loading ? 0.6 : 1
                            }}
                        >
                            {loading ? (
                                <>
                                    <i className="fa fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <i className="fa fa-save" style={{ marginRight: '8px' }}></i>
                                    Save KM Record
                                </>
                            )}
                        </button>

                        <div style={styles.syncNotice}>
                            <i className="fa fa-exchange" style={{ marginRight: '6px', color: '#1976d2' }}></i>
                            KM data will be saved in system and synced to FleetP
                        </div>
                    </form>
                </div>

                {/* Recent Records Section */}
                <div style={styles.card}>
                    <h3 style={styles.cardTitle}>
                        <i className="fa fa-history" style={{ marginRight: '8px', color: '#17a2b8' }}></i>
                        Recent KM Records
                    </h3>
                    
                    {getRecentRecords().length === 0 ? (
                        <div style={styles.emptyState}>
                            <i className="fa fa-inbox" style={{ fontSize: '40px', color: '#ccc', marginBottom: '10px' }}></i>
                            <p>No KM records yet</p>
                        </div>
                    ) : (
                        <div style={styles.tableContainer}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Date</th>
                                        <th style={styles.th}>Driver</th>
                                        <th style={styles.th}>Vehicle</th>
                                        <th style={styles.th}>KM</th>
                                        <th style={styles.th}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getRecentRecords().map(record => (
                                        <tr key={record.id}>
                                            <td style={styles.td}>{record.date}</td>
                                            <td style={styles.td}>
                                                <div style={styles.driverCell}>
                                                    <strong>{record.driverName}</strong>
                                                </div>
                                            </td>
                                            <td style={styles.td}>{record.vehiclePlate}</td>
                                            <td style={styles.td}>
                                                <strong style={{ color: '#1976d2' }}>
                                                    {record.kmReading?.toLocaleString()} km
                                                </strong>
                                            </td>
                                            <td style={styles.td}>{getSyncStatusBadge(record.syncStatus)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    container: {
        padding: '20px',
        maxWidth: '1200px',
        margin: '0 auto',
    },
    header: {
        marginBottom: '24px',
    },
    title: {
        fontSize: '24px',
        fontWeight: '600',
        color: '#1a1a2e',
        margin: '0 0 8px 0',
        display: 'flex',
        alignItems: 'center',
    },
    subtitle: {
        fontSize: '14px',
        color: '#666',
        margin: 0,
    },
    statsRow: {
        display: 'flex',
        gap: '16px',
        marginBottom: '20px',
    },
    statCard: {
        flex: 1,
        backgroundColor: '#fff',
        borderRadius: '10px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    },
    statIcon: {
        width: '50px',
        height: '50px',
        borderRadius: '10px',
        backgroundColor: '#f0f7ff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    statContent: {},
    statValue: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#1a1a2e',
    },
    statLabel: {
        fontSize: '13px',
        color: '#666',
    },
    gridContainer: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '20px',
    },
    card: {
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        padding: '24px',
    },
    cardTitle: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#333',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
    },
    formGroup: {
        marginBottom: '16px',
    },
    formRow: {
        display: 'flex',
        gap: '16px',
        marginBottom: '16px',
    },
    formGroupHalf: {
        flex: 1,
    },
    label: {
        display: 'block',
        fontSize: '14px',
        fontWeight: '500',
        color: '#333',
        marginBottom: '6px',
    },
    input: {
        width: '100%',
        padding: '10px 12px',
        fontSize: '14px',
        border: '1px solid #ddd',
        borderRadius: '6px',
        outline: 'none',
        boxSizing: 'border-box',
    },
    select: {
        width: '100%',
        padding: '10px 12px',
        fontSize: '14px',
        border: '1px solid #ddd',
        borderRadius: '6px',
        outline: 'none',
        boxSizing: 'border-box',
        backgroundColor: '#fff',
    },
    textarea: {
        width: '100%',
        padding: '10px 12px',
        fontSize: '14px',
        border: '1px solid #ddd',
        borderRadius: '6px',
        outline: 'none',
        boxSizing: 'border-box',
        resize: 'vertical',
    },
    totalKmBox: {
        backgroundColor: '#e3f2fd',
        borderRadius: '8px',
        padding: '16px',
        textAlign: 'center',
        marginBottom: '16px',
    },
    totalKmLabel: {
        fontSize: '13px',
        color: '#1976d2',
        marginBottom: '4px',
    },
    totalKmValue: {
        fontSize: '28px',
        fontWeight: '700',
        color: '#1976d2',
    },
    submitButton: {
        width: '100%',
        padding: '12px 20px',
        fontSize: '15px',
        fontWeight: '500',
        color: '#fff',
        backgroundColor: '#1976d2',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    syncNotice: {
        marginTop: '12px',
        padding: '10px 12px',
        backgroundColor: '#e3f2fd',
        borderRadius: '8px',
        fontSize: '13px',
        color: '#1565c0',
        textAlign: 'center',
    },
    successAlert: {
        padding: '12px 16px',
        backgroundColor: '#d4edda',
        color: '#155724',
        borderRadius: '8px',
        marginBottom: '20px',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
    },
    errorAlert: {
        padding: '12px 16px',
        backgroundColor: '#f8d7da',
        color: '#721c24',
        borderRadius: '8px',
        marginBottom: '20px',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
    },
    tableContainer: {
        overflowX: 'auto',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
    },
    th: {
        textAlign: 'left',
        padding: '12px 8px',
        backgroundColor: '#f8f9fa',
        borderBottom: '2px solid #dee2e6',
        fontSize: '12px',
        fontWeight: '600',
        color: '#495057',
    },
    td: {
        padding: '10px 8px',
        borderBottom: '1px solid #dee2e6',
        fontSize: '13px',
        color: '#333',
    },
    driverCell: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
    },
    syncedBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 6px',
        backgroundColor: '#d4edda',
        color: '#155724',
        borderRadius: '10px',
        fontSize: '10px',
        fontWeight: '500',
    },
    pendingBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 6px',
        backgroundColor: '#fff3cd',
        color: '#856404',
        borderRadius: '10px',
        fontSize: '10px',
        fontWeight: '500',
    },
    emptyState: {
        textAlign: 'center',
        padding: '40px 20px',
        color: '#666',
    },
};
